import { Button, ConfirmQuestion, Input } from "components";
import { HeaderWithBack } from "components/HeaderBack";
import { useFormik } from "formik";
import { useNavigate, useParams } from "react-router-dom";

import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { getPartnerUuid } from "services/auth";
import { createOrUpdateDriver, getDriver } from "services/driver";
import * as Yup from "yup";
import styles from "./styles.module.scss";

const DriverSchema = Yup.object().shape({
	name: Yup.string().required("Campo obrigatório"),
	driver_license: Yup.string()
		.required("Campo obrigatório")
		.matches(/^(\d{11}|\d{3}\.\d{3}\.\d{3}-\d{2})$/, "Campo inválido"),
	driver_license_expiration: Yup.date().required("Campo obrigatório"),
	phone: Yup.string()
		.matches(/^\(?\d{2}\)? ?\d{4,5}-?\d{4} *$/, "Campo inválido")
		.required("Campo obrigatório"),
	is_bilingual: Yup.boolean().required("Campo obrigatório")
});

type UpsertDriverFormValues = {
	id: string;
	name: string;
	driver_license: string;
	driver_license_expiration: string;
	phone: string;
	is_bilingual: boolean | null;
};

export default function UpsertDriverPage() {
	const navigate = useNavigate();

	const [partnerId, setPartnerId] = useState("");

	const [submitting, setSubmitting] = useState<boolean>(false);

	const [driver, setDriver] = useState<UpsertDriverFormValues>({
		id: "",
		name: "",
		driver_license: "",
		driver_license_expiration: "",
		phone: "",
		is_bilingual: null
	});

	const params = useParams();
	const driverId = params.driverId || null;

	const fetchData = async () => {
		const partnerUuid = (await getPartnerUuid()) || "";
		setPartnerId(partnerUuid);

		const apiDriver: UpsertDriverFormValues =
			(await getDriver(partnerUuid, driverId)) || driver;

		setDriver(apiDriver);
	};

	useEffect(() => {
		fetchData();
	}, []);

	const formik = useFormik<UpsertDriverFormValues>({
		initialValues: driver,
		validationSchema: DriverSchema,
		onSubmit: async (form) => {
			setSubmitting(true);

			const formattedValidity = form.driver_license_expiration
				? new Date(form.driver_license_expiration).toISOString().slice(0, 10)
				: "";

			const driverResult: any = await createOrUpdateDriver(
				partnerId,
				driverId,
				{
					...form,
					driver_license_expiration: formattedValidity
				}
			).catch((error) => {
				setSubmitting(false);
				let message = "Algo saiu errado. ";

				if (error.code < 500) {
					message += "Verifique os dados digitados e tente novamente.";
					if (error.body.name) formik.errors.name = error.body.name;
					if (error.body.driver_license)
						if (
							error.body.driver_license[0] ===
							"Motorista with this driver license already exists."
						)
							formik.errors.driver_license =
								"Já existe um motorista com esta CNH";
						else formik.errors.driver_license = error.body.driver_license;
					if (error.body.driver_license_expiration)
						formik.errors.driver_license_expiration =
							error.body.driver_license_expiration;
					if (error.body.phone) formik.errors.phone = error.body.phone;
					if (error.body.is_bilingual)
						formik.errors.is_bilingual = error.body.is_bilingual;
				} else {
					message += "Entre em contato com o suporte.";
				}

				toast.error(message, {
					position: "bottom-center",
					autoClose: 5000,
					hideProgressBar: true,
					closeOnClick: true,
					pauseOnHover: true,
					draggable: true,
					progress: undefined,
					theme: "light"
				});
			});

			const driver_id = driverResult?.id;
			if (!driver_id) {
				return;
			}

			navigate("/cadastro/motoristas");
		},
		enableReinitialize: true
	});

	return (
		<div className={styles.container}>
			<HeaderWithBack label="Nova motorista" backTo={-1} />
			<form className={styles.form} onSubmit={formik.handleSubmit}>
				<div className={styles.upsertContent}>
					<Input
						id="name"
						name="name"
						placeholder="Digite o nome do motorista"
						label="Nome do motorista"
						value={formik.values.name}
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
						error={formik.touched.name && formik.errors.name}
						errorMessage={formik.errors.name}
					/>
					<Input
						id="driver_license"
						name="driver_license"
						placeholder="Digite o número da CNH"
						label="CNH"
						value={formik.values.driver_license}
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
						error={
							formik.touched.driver_license && formik.errors.driver_license
						}
						errorMessage={formik.errors.driver_license}
					/>
					<Input
						id="driver_license_expiration"
						name="driver_license_expiration"
						type="date"
						placeholder="dd/mm/aaaa"
						label="Data de validade da CNH"
						value={formik.values.driver_license_expiration}
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
						error={
							formik.touched.driver_license_expiration &&
							formik.errors.driver_license_expiration
						}
						errorMessage={formik.errors.driver_license_expiration}
					/>
					<Input
						id="phone"
						name="phone"
						placeholder="xx xxxxx-xxxx"
						label="Telefone celular"
						value={formik.values.phone}
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
						error={formik.touched.phone && formik.errors.phone}
						errorMessage={formik.errors.phone}
					/>
					<ConfirmQuestion
						value={formik.values.is_bilingual}
						onChange={(value) => formik.setFieldValue("is_bilingual", value)}
						legend="Motorista é bilíngue?"
					/>
				</div>
				<div className={styles.buttonWrapper}>
					<Button type="submit">Salvar</Button>
				</div>
			</form>
		</div>
	);
}
